import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import {
  MAX_RANGE_MONTHS,
  PERIOD_PRESETS,
  PERIOD_TYPE,
} from "./invoiceExport.constants";
import {
  buildExportPayload,
  formatPeriod,
  resolvePresetRange,
  validateRange,
} from "./invoiceExport.utils";
import { useCreateInvoiceExport } from "./invoiceExport.hook";

const datePickerSlotProps = {
  textField: { size: "small", fullWidth: true },
};

/**
 * Date-range picker for requesting a bulk invoice export.
 *
 * Generation is asynchronous, so submitting only queues the job — the user is
 * pointed at the Download section rather than being given a file.
 */
export const InvoiceExportRequestDialog = ({ open, onClose }) => {
  const { teamId } = useSelectedTeam();
  const [periodType, setPeriodType] = useState(PERIOD_TYPE.LAST_MONTH);
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);

  const isCustom = periodType === PERIOD_TYPE.CUSTOM;

  const resetAndClose = () => {
    setPeriodType(PERIOD_TYPE.LAST_MONTH);
    setCustomFrom(null);
    setCustomTo(null);
    onClose?.();
  };

  const { mutate: createExport, isLoading } = useCreateInvoiceExport({
    teamId,
    onSuccess: resetAndClose,
  });

  // The effective range, whether it came from a preset or the custom pickers.
  const effectiveRange = useMemo(
    () =>
      isCustom
        ? { from: customFrom, to: customTo }
        : resolvePresetRange(periodType),
    [isCustom, customFrom, customTo, periodType]
  );

  // Presets are always valid by construction, so only custom ranges are checked
  // before the user has finished picking both dates.
  const validationError = useMemo(() => {
    if (!isCustom) return null;
    if (!customFrom || !customTo) return null;
    return validateRange({ from: customFrom, to: customTo });
  }, [isCustom, customFrom, customTo]);

  const canSubmit =
    !isLoading &&
    !validationError &&
    (!isCustom || (Boolean(customFrom) && Boolean(customTo)));

  const handleSubmit = () => {
    const payload = buildExportPayload({ periodType, customFrom, customTo });
    if (!payload) return;
    // Re-validate on submit: presets are safe, but this also guards a custom
    // range that was edited between render and click.
    const error = validateRange({
      from: new Date(payload.periodFrom),
      to: new Date(payload.periodTo),
    });
    if (error) return;
    createExport(payload);
  };

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="sm" fullWidth>
      <DialogTitle>Download invoices</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Choose a period and we'll prepare a zip of all invoices for it. You
            can request up to {MAX_RANGE_MONTHS} months at a time.
          </Typography>

          <FormControl fullWidth size="small">
            <InputLabel id="invoice-export-period-label">Period</InputLabel>
            <Select
              labelId="invoice-export-period-label"
              label="Period"
              value={periodType}
              onChange={(event) => setPeriodType(event.target.value)}
            >
              {PERIOD_PRESETS.map((preset) => (
                <MenuItem key={preset.value} value={preset.value}>
                  {preset.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isCustom && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ width: "100%" }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <DatePicker
                    label="From"
                    value={customFrom}
                    onChange={setCustomFrom}
                    disableFuture
                    maxDate={customTo ?? undefined}
                    slotProps={datePickerSlotProps}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <DatePicker
                    label="To"
                    value={customTo}
                    onChange={setCustomTo}
                    disableFuture
                    minDate={customFrom ?? undefined}
                    slotProps={datePickerSlotProps}
                  />
                </Box>
              </Stack>
            </LocalizationProvider>
          )}

          {!isCustom && effectiveRange && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Selected period
              </Typography>
              <Typography variant="body2">
                {formatPeriod(effectiveRange.from, effectiveRange.to)}
              </Typography>
            </Box>
          )}

          {validationError && <Alert severity="warning">{validationError}</Alert>}

          <Alert severity="info">
            Preparing the file happens in the background. Once you submit, check
            the Download section for the result.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose} color="inherit" disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {isLoading ? "Submitting..." : "Request download"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceExportRequestDialog;
